
import { computed, defineComponent, SetupContext, ComponentPublicInstance, ref, onMounted, watch, toRefs, nextTick } from "vue";
import NativeClient from "@/client/native-client";
import { translateContententTypeToPageID } from "@/utils/fridgeDetails-utils";
import { BeaconLogInfo } from "@/types/common";
import { generateFridgeSettingsPopupWebContentURL } from "@/utils/web_content_utils";

export default defineComponent({
  name: "FridgeDetailsSettingsHelpDialog",
  props: {
    /** ダイアログのアクティブ状態 (true: 活性、false: 非活性) */
    isShowDialog: {
      type: Boolean,
      default: false
    },
    /** コンテンツのタイプ */
    contentsType: {
      type: String,
      required: true
    },
    /** 品番 */
    productCode: {
      type: String,
      required: true
    }
  },
  setup(props, context: SetupContext) {
    const closeText = "閉じる";
    const S202PType0ProductCode = [
        "5WPX", "5HPX", "6WPX", "6HPX", "7WPX", "7HPX", "8WPX", "8HPX", "8PX", "9WPX", "9HXP", "9PX", "WX1", "HX1", "PX1",
    ];
    const S205PType0ProductCode = [
        "5WPX", "5HPX", "6WPX", "6HPX", "7WPX", "7HPX", "8WPX", "8HPX", "9WPX", "9HPX", "WX1", "HX1", "HV1", "CV1", 
    ];
    const S205PType1ProductCode = [
        "8PX", "9PX", "PX1"
    ];
    const decideIframeContents = () => {
      let isIFrameContents = false;
      switch (props.contentsType) {
        case "S101P":
        case "S102P":
        case "S103P":
        case "S202P":
        case "S205P":
          isIFrameContents = true;
          break;
        default:
          break;
      }
      return isIFrameContents;
    };

    // 表示／非表示の監視
    const { isShowDialog } = toRefs(props);
    const communicationId = ref("");
    const isUseContents = ref(false);
    watch(isShowDialog, () => {
      NativeClient.sendDebugLog("FridgeDetailsSettingsHelpDialog watch");
      if (isShowDialog.value) {
        const pageId = translateContententTypeToPageID(props.contentsType);
        NativeClient.sendDebugLog("FridgeDetailsSettingsHelpDialog watch log pageID = " + pageId);
        const logInfo = new BeaconLogInfo(pageId, null, []);
        NativeClient.sendViewedLog(logInfo);
        if (decideIframeContents()) {
          isUseContents.value = true;
          // ダイアログ全体のDOMが生成されるのを待つ
          nextTick(() => {
            const iframeElement = document.getElementById("dialog-box-contents-iframe-id") as HTMLIFrameElement;
            if (iframeElement != undefined && iframeElement != null) {
              NativeClient.sendDebugLog("FridgeDetailsSettingsHelpDialog isUseContents iframeElement exist");
              const pageId = translateContententTypeToPageID(props.contentsType);
              const url = generateFridgeSettingsPopupWebContentURL(pageId, communicationId.value);
              NativeClient.sendDebugLog("FridgeDetailsSettingsHelpDialog isUseContrents srcUrl = " + url);
              // 履歴が使用されることを避けるために、replaceにしておく
              iframeElement.contentWindow?.location.replace(url);
            }
          });
        } else {
          isUseContents.value = false;
        }
      }
    });

    //
    // iframe関連
    //
    const scrollIframe = ref<ComponentPublicInstance<HTMLInputElement>>();
    const scrollIframeToTop = () => {
      if (scrollIframe.value != undefined && scrollIframe.value != null) {
        scrollIframe.value?.scrollTo({top: 0, left: 0});
      }
    };

    const scroll = ref<ComponentPublicInstance<HTMLInputElement>>();
    const scrollToTop = () => {
      if (scroll.value != undefined && scroll.value != null) {
        scroll.value?.scrollTo({top: 0, left: 0});
      }
    };

    const decisionS202PType0 = () => {
      const find = S202PType0ProductCode.find(element => props.productCode.includes(element));
      if (find == undefined) {
        return false;
      }
      return true;
    };

    const decisionS205PType = () => {
      const findType0 = S205PType0ProductCode.find(element => props.productCode.includes(element));
      if (findType0 != undefined) {
        return 0;
      }
      const findType1 = S205PType1ProductCode.find(element => props.productCode.includes(element));
      if (findType1 != undefined) {
        return 1;
      }
      return 2;
    };

    const closeClicked = () => {
      scrollToTop();
      scrollIframeToTop();

      // iframeが存在すれば、その中身をabout:blankに書き換えておく
      const iframeElement = document.getElementById("dialog-box-contents-iframe-id") as HTMLIFrameElement;
      if (iframeElement != undefined && iframeElement != null) {
        NativeClient.sendDebugLog("FridgeDetailsSettingsHelpDialog closeClicked iframeElement exist");
        // 履歴が使用されることを避けるために、replaceにしておく
        iframeElement.contentWindow?.location.replace("about:blank");
      }

      context.emit("click-close");
    };

    const displayTitleTable = [
      { type: "S101P", title: "パーシャル/チルド切替" },
      { type: "S102P", title: "クイック操作ボタン" },
      { type: "S103P", title: "パーシャル/冷凍切替" },
      { type: "S201P", title: "冷蔵" },
      { type: "S202P", title: "冷凍" },
      { type: "S203P", title: "速氷/製氷停止" },
      { type: "S205P", title: "詳細設定" },
      { type: "S206P", title: "新鮮凍結" },
      { type: "S207P", title: "パーシャル半解凍" }
    ];
    const displayTitle = computed(() => {
      const find = displayTitleTable.find(element => props.contentsType == element.type);
      if (find != undefined) {
        return find.title;
      }
      return "";
    });
    const contentsImage = computed(() => {
      NativeClient.sendDebugLog("FridgeDetailsSettingsHelpDialog contentsImage contentsType = " + props.contentsType + ", productCode = " + props.productCode);
      switch (props.contentsType) {
        case "S101P":
          return require("@/assets/images/dialog_image_S101P.png");
        case "S102P":
          return require("@/assets/images/dialog_image_S102P.png");
        case "S201P":
          return require("@/assets/images/dialog_image_S201P.png");
        case "S202P":
          if (decisionS202PType0()) {
            return require("@/assets/images/dialog_image_S202P_WX1HX1PX1.png");
          }
          return require("@/assets/images/dialog_image_S202P_V1.png");
        case "S203P":
          return require("@/assets/images/dialog_image_S203P.png");
        case "S205P":
          {
          const type = decisionS205PType();
          switch (type) {
            case 0:
              return require("@/assets/images/dialog_image_S205P_WX1.png");
            case 1:
              return require("@/assets/images/dialog_image_S205P_PX1.png");
            default:
              return  require("@/assets/images/noimage1.png");
            }
          }
        case "S206P":
          NativeClient.sendDebugLog("");
          return require("@/assets/images/dialog_image_S206P.png");
        case "S207P":
          return require("@/assets/images/dialog_image_S207P.png");
        default:
          break;
        }
        return require("@/assets/images/noimage.png")
    });

    onMounted(() => {
      NativeClient.sendDebugLog("FridgeDetailsSettingsHelpDialog onMounted");
      NativeClient.getProductCommunicationID().then((result) => {
        NativeClient.sendDebugLog("FridgeDetailsSettingsHelpDialog onMounted getProductCommunicationID result = " + `${result}`);
        communicationId.value = `${result}`;
      });
    });

    return {
      displayTitle,
      contentsImage,
      closeText,
      closeClicked,
      scroll,
      scrollIframe,
      isUseContents
    };
}
});
